import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'b1link:menu-naming-v2';
const LEGACY_VALUE = 'legacy';

/**
 * The two navigation section labels that were renamed under #6096
 * ("Company" → "Network", "Office" → "Work"). `new` is the current default;
 * `legacy` is what the toggle restores.
 */
export const MENU_LABELS = {
  new: { network: 'Network', work: 'Work' },
  legacy: { network: 'Company', work: 'Office' }
} as const;

export type MenuLabels = { network: string; work: string };

/**
 * Non-reactive read of the flag. `true` = new (renamed) labels (default),
 * `false` = legacy labels. Backed by origin-isolated `localStorage`: it is scoped
 * to this origin, served only over HTTPS in deployed environments, and — unlike a
 * cookie — is never transmitted to the server. Safe for a per-device UI preference.
 * Useful in non-hook contexts (e.g. computed values outside a component).
 */
export function getUseNewMenuNames(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== LEGACY_VALUE;
  } catch {
    return true; // storage unavailable (private mode / disabled) → default to new names
  }
}

// ── Tiny pub/sub so every mounted hook instance re-renders on toggle ──
const listeners = new Set<() => void>();
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function notify() {
  listeners.forEach((cb) => cb());
}

/**
 * App-wide toggle for the navigation section labels (#6096). The control lives in the
 * hamburger menu; the preference is shared, so the mobile hamburger and the desktop
 * navigation switch together via `useSyncExternalStore`, and it persists across reloads
 * in origin-isolated `localStorage` (see {@link getUseNewMenuNames}).
 *
 * Modelled on `useOnboardingLayoutToggle` — the established house pattern for a
 * device-local UI preference toggle.
 */
export function useMenuNamingToggle() {
  const useNewMenuNames = useSyncExternalStore(subscribe, getUseNewMenuNames, () => true);

  const setUseNewMenuNames = useCallback((next: boolean) => {
    try {
      if (next) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, LEGACY_VALUE);
      }
    } catch {
      // storage unavailable — silently ignore; the in-memory render still updates
    }
    notify();
  }, []);

  const toggle = useCallback(
    () => setUseNewMenuNames(!getUseNewMenuNames()),
    [setUseNewMenuNames]
  );

  const labels: MenuLabels = useNewMenuNames ? MENU_LABELS.new : MENU_LABELS.legacy;

  return { useNewMenuNames, toggle, setUseNewMenuNames, labels } as const;
}
